import React, { useState, useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import {
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  Bars3Icon,
  XMarkIcon,
  PaintBrushIcon,          // Added for new Arena
  BuildingStorefrontIcon,  // Added for new Arena
  ArrowRightIcon           // Added for new Arena
} from '@heroicons/react/24/outline';

const MainLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const aboutSectionRef = useRef<HTMLDivElement>(null);
  const [isMuted, setIsMuted] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);

  const handlePainterArena = () => navigate('/login');

  const handleDealerArena = () => {
    window.open('https://scidealer.web.app', '_blank', 'noopener,noreferrer');
  };

  const handleLearnMore = () => {
    window.open('https://scipaints.in/about-us/', '_blank', 'noopener');
  };

  const scrollToAbout = () => {
    aboutSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleUnmuteInteraction = () => {
    if (videoRef.current) {
      videoRef.current.muted = false;
      setIsMuted(false);
      videoRef.current.play().catch(e => console.log('Playback error:', e));
    }
  };

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = true;
      videoRef.current.play().catch(() => { });
    }

    const enableAudio = () => {
      handleUnmuteInteraction();
      document.removeEventListener('click', enableAudio);
      document.removeEventListener('touchstart', enableAudio);
    };

    document.addEventListener('click', enableAudio);
    document.addEventListener('touchstart', enableAudio);

    return () => {
      document.removeEventListener('click', enableAudio);
      document.removeEventListener('touchstart', enableAudio);
    };
  }, []);

  return (
    <div className="min-h-screen bg-transparent w-full">
      <Helmet>
        <title>SCI Paints BONDHU | Official Painter Loyalty & Rewards Platform</title>
        <meta name="description" content="Welcome to SCI Paints BONDHU, the official and exclusive digital ecosystem for painters. Join our loyalty program to scan tokens and earn cashback rewards." />
      </Helmet>

      {/* --- HEADER --- */}
      <header className="bg-white text-black py-3 px-4 fixed top-0 left-0 right-0 z-50 shadow-sm h-[60px] lg:h-[70px] flex items-center">
        <div className="flex justify-between items-center w-full">
          <div className="flex items-center">
            <h1 className="sr-only" style={{ position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap', border: 0 }}>SCI Paints BONDHU</h1>
            <img src="/SCI Paints Bandhu Logo Final.png" alt="SCI Paints BONDHU" className="h-10 sm:h-12 w-auto" />
          </div>

          <nav className="hidden md:flex space-x-6 lg:space-x-8">
            {['Products', 'Colours', 'About', 'Contact'].map((item) => (
              <a
                key={item}
                href={`https://scipaints.in/${item.toLowerCase().replace(' ', '-')}/`}
                target="_blank"
                rel="noopener"
                className="text-gray-700 hover:text-[#ee4238] transition-colors font-medium text-sm lg:text-base"
              >
                {item}
              </a>
            ))}
          </nav>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            {isMobileMenuOpen ? <XMarkIcon className="w-6 h-6" /> : <Bars3Icon className="w-6 h-6" />}
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-white border-t border-gray-100 shadow-lg">
            <nav className="flex flex-col p-4 space-y-3">
              {['Products', 'Colours', 'About', 'Contact'].map((item) => (
                <a
                  key={item}
                  href={`https://scipaints.in/${item.toLowerCase().replace(' ', '-')}/`}
                  target="_blank"
                  rel="noopener"
                  className="text-gray-700 hover:text-[#ee4238] hover:bg-gray-50 px-4 py-2 rounded-lg transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item}
                </a>
              ))}
            </nav>
          </div>
        )}
      </header>

      {/* --- HERO VIDEO SECTION --- */}
      <section className="relative w-full bg-black pt-[60px] lg:pt-[70px] flex flex-col justify-start items-center">
        <div className="relative w-full">
          <video
            ref={videoRef}
            src="/SCIPAINT_VIDEO.mp4"
            className="w-full h-auto block"
            autoPlay
            loop
            muted
            playsInline
            webkit-playsinline="true"
            preload="metadata"
          />

          {/* Overlay for interaction */}
          <div
            onClick={handleUnmuteInteraction}
            className="absolute inset-0 z-10 bg-transparent cursor-pointer"
          />

          {/* Scroll Button */}
          <div className="absolute bottom-4 sm:bottom-8 left-0 right-0 z-20 px-4 flex justify-center pointer-events-none">
            <button
              onClick={(e) => {
                e.stopPropagation();
                scrollToAbout();
              }}
              className="pointer-events-auto bg-black/30 backdrop-blur-sm text-white py-2 px-6 sm:py-3 sm:px-8 rounded-full font-semibold hover:bg-black/50 transition-all duration-300 border border-white/20 shadow-lg scale-90 sm:scale-100"
            >
              <div className="flex flex-col items-center">
                <span className="text-xs sm:text-sm mb-1 drop-shadow-md">Scroll Down</span>
                <svg className="w-4 h-4 sm:w-5 sm:h-5 drop-shadow-md" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
              </div>
            </button>
          </div>
        </div>
      </section>

      {/* --- ABOUT SECTION --- */}
      <section ref={aboutSectionRef} className="bg-white py-12 lg:py-20 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div className="flex flex-col justify-center order-2 md:order-1">
            <h3 className="text-2xl lg:text-4xl font-bold text-black mb-6 text-center md:text-left">About SCI Paints BONDHU</h3>
            <div className="relative">
              <p className={`text-gray-700 leading-relaxed text-center md:text-left text-base lg:text-lg transition-all duration-500 ${!isExpanded ? 'line-clamp-4 overflow-hidden' : ''}`}>
                Welcome to SCI Paints BONDHU, the official and exclusive digital ecosystem designed for the visionary painters and dealers of the SCI Paints family. As a pioneer in the Indian coatings industry, SCI Paints has always stood for unmatched quality, scientific innovation, and deep-rooted trust. The BONDHU platform—our flagship loyalty and rewards program—is more than just an app; it is a gateway to a prosperous partnership. Here, painters can effortlessly scan unique scientific tokens found within our premium product line, from exquisite interior emulsions to high-durability industrial coatings, instantly converting their hard work into real-time cashback and redeemable reward points.
                <br /><br />
                Our platform is built on transparency, ensuring that every drop of paint you apply translates into tangible value for your career and family. Beyond rewards, SCI Paints BONDHU serves as a comprehensive knowledge hub, providing instant access to our latest product catalogs, trend-setting color combinations, and expert support, all tailored to empower the modern contractor. Join us in our mission to not just paint walls, but to build lasting bonds of success and scientific excellence across India. We believe in empowering our partners with the tools and data they need to thrive in a competitive market, combining traditional craftsmanship with modern technology.
              </p>
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="mt-2 text-[#ee4238] font-bold hover:underline focus:outline-none block mx-auto md:mx-0"
              >
                {isExpanded ? 'Show Less' : 'Show More'}
              </button>
            </div>
            <div className="flex flex-col sm:flex-row items-center gap-4 mt-8">
              <button
                onClick={handleLearnMore}
                className="w-full sm:w-auto bg-[#ee4238] text-white px-8 py-3 rounded-full font-semibold hover:bg-[#d63a32] transition-colors shadow-md hover:shadow-lg"
              >
                Learn More
              </button>
            </div>
          </div>
          <div className="order-1 md:order-2 h-64 sm:h-80 lg:h-96 rounded-2xl overflow-hidden shadow-xl">
            <img
              src="/STANDING.png"
              alt="Our team"
              className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-700"
            />
          </div>
        </div>
      </section>

      {/* --- ARENA SECTIONS (NEW & STYLISH) --- */}
      <section className="bg-gray-50 py-16 lg:py-24 px-4 sm:px-6 relative overflow-hidden">
        {/* Background decorative blob */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          <div className="absolute top-10 left-10 w-64 h-64 bg-red-100 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob"></div>
          <div className="absolute top-10 right-10 w-64 h-64 bg-orange-100 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-2000"></div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">Choose Your Arena</h2>
            <p className="text-gray-500 max-w-2xl mx-auto">Select your role to access exclusive dashboards, rewards, and tools tailored just for you.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 lg:gap-12 max-w-5xl mx-auto">

            {/* 1. PAINTER ARENA CARD */}
            <div
              onClick={handlePainterArena}
              className="group relative h-[350px] rounded-[2rem] overflow-hidden cursor-pointer transition-all duration-500 hover:-translate-y-3 hover:shadow-[0_20px_50px_rgba(238,66,56,0.3)]"
            >
              {/* Card Background Gradient */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#ee4238] to-[#c02e25] transition-transform duration-500 group-hover:scale-105"></div>

              {/* Background Watermark Icon */}
              <PaintBrushIcon className="absolute -right-12 -bottom-12 w-64 h-64 text-white opacity-10 transform rotate-12 group-hover:rotate-0 group-hover:scale-110 transition-all duration-700 ease-out" />

              {/* Glass Overlay Effect */}
              <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-5 transition-opacity duration-300"></div>

              {/* Content */}
              <div className="relative h-full flex flex-col justify-between p-8 md:p-10 text-white">
                <div>
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm mb-6 border border-white/30">
                    <PaintBrushIcon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-3xl md:text-4xl font-bold tracking-wide mb-3 uppercase">
                    Painter Arena
                  </h3>
                  <p className="text-red-100 font-medium text-base md:text-lg opacity-90">
                    Scan tokens, earn points, and redeem exciting rewards.
                  </p>
                </div>

                {/* Button */}
                <div className="flex items-center font-bold text-lg group/btn">
                  <span className="bg-white text-[#ee4238] px-8 py-3 rounded-full shadow-lg group-hover/btn:pr-12 transition-all duration-300 relative overflow-hidden">
                    Join Now
                    <ArrowRightIcon className="w-5 h-5 absolute right-4 top-1/2 -translate-y-1/2 opacity-0 -translate-x-4 group-hover/btn:opacity-100 group-hover/btn:translate-x-0 transition-all duration-300" />
                  </span>
                </div>
              </div>
            </div>

            {/* 2. DEALER ARENA CARD */}
            <div
              onClick={handleDealerArena}
              className="group relative h-[350px] rounded-[2rem] overflow-hidden cursor-pointer transition-all duration-500 hover:-translate-y-3 hover:shadow-[0_20px_50px_rgba(30,41,59,0.3)]"
            >
              {/* Card Background - Dark Premium Theme for Contrast */}
              <div className="absolute inset-0 bg-gradient-to-br from-slate-800 to-slate-900 transition-transform duration-500 group-hover:scale-105"></div>

              {/* Background Watermark Icon */}
              <BuildingStorefrontIcon className="absolute -right-12 -bottom-12 w-64 h-64 text-white opacity-5 transform -rotate-12 group-hover:rotate-0 group-hover:scale-110 transition-all duration-700 ease-out" />

              {/* Red Accent Line */}
              <div className="absolute top-0 left-0 w-full h-1.5 bg-[#ee4238]"></div>

              {/* Content */}
              <div className="relative h-full flex flex-col justify-between p-8 md:p-10 text-white">
                <div>
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm mb-6 border border-white/20">
                    <BuildingStorefrontIcon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-3xl md:text-4xl font-bold tracking-wide mb-3 uppercase">
                    Dealer Arena
                  </h3>
                  <p className="text-slate-300 font-medium text-base md:text-lg opacity-90">
                    Manage orders, track sales, and grow your business network.
                  </p>
                </div>

                {/* Button */}
                <div className="flex items-center font-bold text-lg group/btn">
                  <span className="bg-[#ee4238] text-white px-8 py-3 rounded-full shadow-lg group-hover/btn:pr-12 transition-all duration-300 relative overflow-hidden">
                    Access Portal
                    <ArrowRightIcon className="w-5 h-5 absolute right-4 top-1/2 -translate-y-1/2 opacity-0 -translate-x-4 group-hover/btn:opacity-100 group-hover/btn:translate-x-0 transition-all duration-300" />
                  </span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* --- FOOTER --- */}
      <footer className="bg-[#ee4238] text-white py-12 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          <div>
            <h4 className="text-lg font-bold mb-4 border-b border-white/20 pb-2 inline-block">Quick Links</h4>
            <ul className="space-y-2">
              {[
                { name: 'About Us', url: 'https://scipaints.in/about-us/' },
                { name: 'Products', url: 'https://scipaints.in/products/' },
                { name: 'Colour Combo', url: 'https://scipaints.in/colour-combo/' },
                { name: 'Contact', url: 'https://scipaints.in/contact/' }
              ].map(link => (
                <li key={link.name}>
                  <a href={link.url} target="_blank" rel="noopener noreferrer" className="hover:text-gray-200 hover:translate-x-1 transition-all inline-block">
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-lg font-bold mb-4 border-b border-white/20 pb-2 inline-block">Contact</h4>
            <div className="space-y-4">
              <div className="flex items-center">
                <EnvelopeIcon className="w-5 h-5 mr-3 flex-shrink-0 opacity-80" />
                <span>scipaints.sales@gmail.com</span>
              </div>
              <div className="flex items-center">
                <PhoneIcon className="w-5 h-5 mr-3 flex-shrink-0 opacity-80" />
                <span>+91 9874812501</span>
              </div>
            </div>
          </div>

          <div className="sm:col-span-2 lg:col-span-1">
            <h4 className="text-lg font-bold mb-4 border-b border-white/20 pb-2 inline-block">Address</h4>
            <div className="flex items-start">
              <MapPinIcon className="w-5 h-5 mr-3 mt-1 flex-shrink-0 opacity-80" />
              <span>SCI Paints, Sugandha, West Bengal 712102</span>
            </div>
          </div>
        </div>
      </footer>

      {/* --- LOGO STRIP --- */}
      <section className="bg-black py-4 px-4">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-6">
            <a href="https://scipaints.in/home-solution/" target="_blank" rel="noopener noreferrer" className="hover:scale-110 transition-transform">
              <img src="/HOME_SOLUTION.png" alt="Home Solution" className="h-10 w-auto rounded" />
            </a>
            <a href="https://scipaints.in/" target="_blank" rel="noopener noreferrer" className="hover:scale-110 transition-transform">
              <img src="/SCIPNT_TEXT_LOGO.png" alt="SCI Paints" className="h-10 w-auto rounded" />
            </a>
          </div>
          <p className="text-xs sm:text-sm text-gray-400 text-center md:text-right">
            Copyright © 2026 SCI Paints | Managed By <a href="https://www.facebook.com/mukhoos/" target="_blank" rel="noopener noreferrer" className="text-white hover:text-blue-400 underline">Mukhoos Digital</a>
          </p>
        </div>
      </section>
    </div>
  );
};

export default MainLoginPage;